import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { css } from 'styled-components';

import { FontAwesomeIcon, IconDefinition } from '@sorare/core/src/atoms/icons';
import { Text16 } from '@sorare/core/src/atoms/typography';
import useLoggedCallback from '@sorare/core/src/hooks/useLoggedCallback';
import useVirtualToggle from '@sorare/core/src/hooks/useVirtualToggle';
import { useVirtualToggleManager } from '@sorare/core/src/hooks/useVirtualToggleManager';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { VIRTUAL_TOGGLE_FILTERS } from '@sorare/core/src/lib/filters';

import { SegmentButton } from '../ui';

type Filter =
  (typeof VIRTUAL_TOGGLE_FILTERS)[keyof typeof VIRTUAL_TOGGLE_FILTERS];
type Props = {
  filter: Filter;
  cleanableFilters?: Filter[];
  needsLogin?: boolean;
  onClick: (b: boolean) => void;
  icon: IconDefinition | null;
  customCss?: ReturnType<typeof css>;
};

export const SegmentFilter = ({
  filter,
  cleanableFilters = [],
  icon,
  customCss,
  onClick,
  needsLogin = false,
}: Props) => {
  const track = useEvents();
  const setVirtualToggle = useVirtualToggleManager();
  const { currentRefinement } = useVirtualToggle<boolean>({
    name: filter.name,
  });

  const handleChange = () => {
    const newValue = !currentRefinement;
    setVirtualToggle({
      ...cleanableFilters.reduce(
        (acc, curr) => ({ ...acc, [curr.name]: false }),
        {}
      ),
      [filter.name]: newValue,
    });
    track('Use Market Filter', {
      filterName: filter.trackingName,
      filterValue: newValue.toString(),
    });
    onClick(newValue);
  };

  const loggedHandleChange = useLoggedCallback(handleChange);
  const checked = !!currentRefinement;

  const ref = useRef<HTMLButtonElement>(null!);
  useEffect(() => {
    if (currentRefinement) {
      ref.current?.scrollIntoView();
    }
  }, [currentRefinement]);

  return (
    <SegmentButton
      ref={ref}
      $customCss={customCss}
      className={classNames({ checked })}
      onClick={needsLogin ? loggedHandleChange : handleChange}
    >
      {icon && <FontAwesomeIcon icon={icon} />}
      <Text16 bold>
        {'label' in filter && <FormattedMessage {...filter.label} />}
        {'title' in filter && <FormattedMessage {...filter.title} />}
      </Text16>
    </SegmentButton>
  );
};

export default SegmentFilter;
